import React, { useState, useEffect, useMemo } from 'react';
import { useTable } from 'react-table';
import { FaEdit, FaTrashAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './UpdateHoliday.css';

const UpdateHoliday = () => {
    const navigate = useNavigate();
    const [holidayList, setHolidayList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [newHoliday, setNewHoliday] = useState({ HolidayName: '', HolidayDate: '' });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_AIRPORT_PICKUP_SERVER}/api/getHoliday?_=${new Date().getTime()}`);
            const jsonData = await response.json();
            setHolidayList(jsonData);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching holiday data:', error.message);
        }
    };


    const handleInputChange = (e, index, field) => {
        const updatedList = [...holidayList];
        updatedList[index][field] = e.target.value;
        setHolidayList(updatedList);
    };

    const handleAddHoliday = () => {
        // Ensure both HolidayName and HolidayDate are provided before adding
        if (newHoliday.HolidayName && newHoliday.HolidayDate) {
            const formattedHoliday = {
                HolidayName: newHoliday.HolidayName,
                // Convert datetime string to ISO 8601 format and remove time component
                HolidayDate: new Date(newHoliday.HolidayDate).toISOString().split('T')[0]
                
            };

            setHolidayList([...holidayList, formattedHoliday]);
            setNewHoliday({ HolidayName: '', HolidayDate: '' });
        } else {
            alert('Please provide both Holiday Name and Date');
        }
    };

    const handleDeleteHoliday = (index) => {
        const updatedList = [...holidayList];
        updatedList.splice(index, 1);
        setHolidayList(updatedList);
    };

    const handleSave = async () => {
        // Validate all entries in holidayList
        const isValid = holidayList.every(holiday => holiday.HolidayName && holiday.HolidayDate);

        if (!isValid) {
            alert('Make sure all data is inserted correctly');
            return;
        }

        try {
            const formattedHolidayList = holidayList.map(holiday => ({
                ...holiday,
                HolidayDate: new Date(holiday.HolidayDate).toISOString() // Convert to ISO format
            }));

            const response = await fetch(`${process.env.REACT_APP_AIRPORT_PICKUP_SERVER}/api/updateHoliday`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formattedHolidayList),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error saving holiday data');
            }

            const result = await response.json();
            alert('Data updated successfully');
            navigate('/adminPortal');
        } catch (error) {
            console.error('Error saving holiday data:', error.message);
        }
    };

    const handleCancel = () => {
        navigate('/adminPortal');
    };

    const columns = useMemo(() => [
        {
            Header: 'Holiday Name',
            accessor: 'HolidayName',
            Cell: ({ value, row: { index } }) => (
                <input
                    type="text"
                    value={value}
                    onChange={(e) => handleInputChange(e, index, 'HolidayName')}
                />
            ),
        },
        {
            Header: 'Holiday Date',
            accessor: 'HolidayDate',
            Cell: ({ value, row: { index } }) => (
                <input
                    type="date"
                    value={value}
                    onChange={(e) => handleInputChange(e, index, 'HolidayDate')}
                />
            ),
        },
        {
            Header: 'Actions',
            Cell: ({ row: { index } }) => (
                <div>
      
                    <FaTrashAlt onClick={() => handleDeleteHoliday(index)} />
                </div>
            ),
        },
    ], [holidayList]);

    const tableInstance = useTable({ columns, data: holidayList });

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = tableInstance;


    const handleDateChange = (index, newValue) => {
        const updatedList = [...holidayList];
        updatedList[index].HolidayDate = newValue; // Update the HolidayDate for the specified row
        setHolidayList(updatedList); // Update the holidayList state
    };
    return (
        <div className="container">
            <h2>Holiday List</h2>
            {isLoading ? (
                <p>Loading...</p>
            ) : (
                <div>
                    <table className="table" {...getTableProps()}>
                        <thead>
                            {headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map(column => (
                                        <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {rows.map(row => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()}>
                                        {row.cells.map(cell => (
                                            <td {...cell.getCellProps()}>
                                                {cell.column.id === 'HolidayDate' ? (
                                                    <input
                                                        type="date"
                                                        value={cell.value.slice(0, 10)}
                                                        onChange={(e) => handleDateChange(row.index, e.target.value)}
                                                    />
                                                ) : (
                                                    cell.render('Cell')
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                            <tr>
                                <td>
                                    <input
                                        type="text"
                                        value={newHoliday.HolidayName}
                                        onChange={(e) => setNewHoliday({ ...newHoliday, HolidayName: e.target.value })}
                                        placeholder="Holiday Name"
                                    />
                                </td>
                                <td>
                                    <input
                                        type="date"
                                        value={newHoliday.HolidayDate}
                                        onChange={(e) => setNewHoliday({ ...newHoliday, HolidayDate: e.target.value })}
                                    />
                                </td>
                                <td>
                                    <button className="add-button" onClick={handleAddHoliday}>Add</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div>
                        <button className="save-button" onClick={handleSave}>Save</button>
                        <button className="cancel-button" onClick={handleCancel}>Cancel</button>
                        </div>
                        <div style={{ marginBottom: '100px' }} />
                </div>
            )}
        </div>
    );
};

export default UpdateHoliday;
